import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, CanMatch, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { ProfileService } from 'app/modules/profile/profile.service';
import { AuthService } from 'app/authentication/auth/auth.service';
import { redirectToServerSignIn } from 'app/authentication/auth/auth-redirect.util';
import { AuthUtils } from 'app/authentication/auth/auth.utils';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AdminGuard implements CanActivate, CanActivateChild, CanMatch {
    constructor(
        private _userService: ProfileService,
        private _authService: AuthService,
        private _router: Router
    ) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
        return this._authService.check().pipe(
            switchMap((authenticated: boolean) => {
                if (!authenticated) {
                    redirectToServerSignIn(state.url);
                    return of(false);
                }

                if (this._authService.isWorkspaceSelectionRequired() && !this._authService.hasWorkspaceSelection()) {
                    return of(this._router.createUrlTree(['/dashboard']));
                }

                return this._userService.user$.pipe(
                    take(1),
                    map((user) => {
                        const isAdmin = AuthUtils.isSuperAdminUser(user)
                            || AuthUtils.hasRole(user?.role, ['@SUPERADMIN', '@ADMIN'], user);
                        return isAdmin ? true : this._router.createUrlTree(['/profile']);
                    })
                );
            }),
            catchError(() => {
                redirectToServerSignIn();
                return of(false);
            })
        );
    }

    canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
        return this.canActivate(childRoute, state);
    }

    canMatch(route: Route, segments: UrlSegment[]): Observable<boolean | UrlTree> {
        const targetUrl = `/${segments.map((segment) => segment.path).join('/')}`;

        return this._authService.check().pipe(
            switchMap((authenticated: boolean) => {
                if (!authenticated) {
                    redirectToServerSignIn(targetUrl || '/admin');
                    return of(false);
                }

                if (this._authService.isWorkspaceSelectionRequired() && !this._authService.hasWorkspaceSelection()) {
                    return of(this._router.createUrlTree(['/dashboard']));
                }

                return this._userService.user$.pipe(
                    take(1),
                    map((user) => AuthUtils.isSuperAdminUser(user)
                        || AuthUtils.hasRole(user?.role, ['@SUPERADMIN', '@ADMIN'], user))
                );
            }),
            catchError(() => {
                redirectToServerSignIn();
                return of(false);
            })
        );
    }
}
